import { useState, useEffect } from "react";
import { P, FF } from "../../styles/tokens";
import { Card } from "../ui";
import { useNotifications } from "../../hooks/useNotifications";
import { api } from "../../services/api";
import { track } from "../../lib/posthog";

// ─── Tipos de notificaciones disponibles ────────────────────────────────────
const NOTIF_TYPES = [
  {
    key:   "fijos_vencimiento",
    label: "Gastos fijos por vencer",
    desc:  "Recibe aviso cuando un gasto fijo se acerca a su fecha de pago.",
  },
  {
    key:   "tdc_corte",
    label: "Corte de tarjeta",
    desc:  "Alerta cuando se acerca el día de corte de tu TDC.",
  },
  {
    key:   "tdc_limite_pago",
    label: "Límite de pago TDC",
    desc:  "Aviso antes del día límite para pagar tu tarjeta de crédito.",
  },
  {
    key:   "presupuesto_alerta",
    label: "Alerta de presupuesto",
    desc:  "Notificación cuando estás cerca de agotar un presupuesto.",
  },
  {
    key:   "auto_pago_tdc",
    label: "Auto-pago TDC",
    desc:  "Confirma cuando se realiza un pago automático de tu TDC.",
  },
  {
    key:   "resumen_semanal",
    label: "Resumen semanal",
    desc:  "Recibe un resumen de tus finanzas cada semana.",
  },
  {
    key:   "recordatorio_diario",
    label: "Recordatorio diario",
    desc:  "Un recordatorio diario para registrar tus gastos.",
  },
];

// ─── Toggle individual de preferencia ───────────────────────────────────────
function PreferenceToggle({ notifKey, label, desc, enabled, onChange }) {
  return (
    <div
      style={{
        display:        "flex",
        alignItems:     "flex-start",
        justifyContent: "space-between",
        gap:            12,
        padding:        "10px 0",
        borderBottom:   `1px solid ${P.border}`,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: P.text, fontFamily: FF }}>
          {label}
        </div>
        <div style={{ fontSize: 11, color: P.textSm, fontFamily: FF, marginTop: 2, lineHeight: 1.5 }}>
          {desc}
        </div>
      </div>

      {/* Toggle switch */}
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={label}
        onClick={() => onChange(notifKey, !enabled)}
        style={{
          flexShrink:    0,
          width:         44,
          height:        24,
          borderRadius:  12,
          border:        "none",
          background:    enabled ? P.ingresos.base : P.borderMd,
          cursor:        "pointer",
          position:      "relative",
          transition:    "background .2s",
          padding:       0,
        }}
      >
        <span
          style={{
            display:      "block",
            width:        18,
            height:       18,
            borderRadius: "50%",
            background:   "#fff",
            position:     "absolute",
            top:          3,
            left:         enabled ? 23 : 3,
            transition:   "left .2s",
            boxShadow:    "0 1px 3px rgba(0,0,0,0.2)",
          }}
        />
      </button>
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────
/**
 * NotificationsConfig — Sección de notificaciones para ConfigView.
 *
 * Maneja el flujo completo: verificar soporte, pedir permiso,
 * suscribirse/desuscribirse y gestionar preferencias por tipo.
 */
export default function NotificationsConfig() {
  const {
    isSupported,
    isIos,
    isStandalone,
    permission,
    isSubscribed,
    loading,
    requestPermissionAndSubscribe,
    unsubscribe,
    sendTestNotification,
  } = useNotifications();

  // Preferencias de notificación por tipo
  const [preferences, setPreferences]       = useState({});
  const [prefsLoaded, setPrefsLoaded]       = useState(false);
  const [actionFeedback, setActionFeedback] = useState(""); // mensaje temporal de éxito/error

  // Cargar preferencias del backend cuando la suscripción está activa
  useEffect(() => {
    if (!isSubscribed) return;

    api.notifications
      .getPreferences()
      .then((data) => {
        setPreferences(data || {});
        setPrefsLoaded(true);
      })
      .catch((err) => {
        console.warn("[NotificationsConfig] Error al cargar preferencias:", err);
        setPrefsLoaded(true);
      });
  }, [isSubscribed]);

  // Mostrar feedback temporal
  function showFeedback(msg) {
    setActionFeedback(msg);
    setTimeout(() => setActionFeedback(""), 3000);
  }

  // Manejar suscripción
  async function handleSubscribe() {
    const ok = await requestPermissionAndSubscribe();
    if (ok) {
      try { track("notifications_enabled"); } catch {}
      showFeedback("Notificaciones activadas correctamente.");
    } else {
      showFeedback("No se pudo activar. Revisa los permisos del navegador.");
    }
  }

  // Manejar desuscripción
  async function handleUnsubscribe() {
    await unsubscribe();
    setPreferences({});
    setPrefsLoaded(false);
    showFeedback("Notificaciones desactivadas.");
  }

  // Actualizar preferencia individual con optimistic update
  async function handleTogglePreference(key, value) {
    // Actualización optimista: cambiar en UI de inmediato
    const previous = { ...preferences };
    setPreferences((prev) => ({ ...prev, [key]: value }));

    try {
      await api.notifications.updatePreferences({ [key]: value });
      try { track("notification_pref_changed", { key, value }); } catch {}
    } catch (err) {
      // Revertir si falla el backend
      console.error("[NotificationsConfig] Error al guardar preferencia:", err);
      setPreferences(previous);
      showFeedback("Error al guardar preferencia. Intenta de nuevo.");
    }
  }

  // Manejar notificación de prueba
  async function handleTest() {
    try {
      await sendTestNotification();
      showFeedback("Notificación de prueba enviada.");
    } catch {
      showFeedback("Error al enviar prueba.");
    }
  }

  // ── Caso 1: Navegador no soporta notificaciones ──
  if (!isSupported) {
    return (
      <Card style={{ padding: "18px 20px" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 8 }}>
          Notificaciones
        </div>
        <div
          style={{
            background: P.bg,
            borderRadius: 10,
            padding: "12px 14px",
            fontSize: 13,
            color: P.textSm,
            fontFamily: FF,
            lineHeight: 1.6,
          }}
        >
          Tu navegador no soporta notificaciones push. Para recibirlas, usa Chrome,
          Firefox, Edge o Safari 16.4+ en iOS.
        </div>
      </Card>
    );
  }

  // ── Caso 2: iOS fuera de modo standalone ──
  // En iOS, las notificaciones push solo funcionan si la app está instalada como PWA.
  if (isIos && !isStandalone) {
    return (
      <Card style={{ padding: "18px 20px" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 8 }}>
          Notificaciones
        </div>
        <div
          style={{
            background: P.warn.pastel,
            border:     `1.5px solid ${P.warn.tint}`,
            borderRadius: 12,
            padding:    "14px 16px",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: P.warn.text, fontFamily: FF, marginBottom: 6 }}>
            Agrega Arbo a tu pantalla de inicio
          </div>
          <div style={{ fontSize: 12, color: P.warn.text, fontFamily: FF, lineHeight: 1.7 }}>
            Para recibir notificaciones en iPhone o iPad, primero instala Arbo como app:{"\n"}
            <br />
            1. Toca el botón <strong>Compartir</strong> en Safari (cuadro con flecha).
            <br />
            2. Selecciona <strong>"Agregar a pantalla de inicio"</strong>.
            <br />
            3. Abre Arbo desde el ícono instalado y activa las notificaciones aquí.
          </div>
        </div>
      </Card>
    );
  }

  // ── Caso 3: Permiso bloqueado explícitamente por el usuario ──
  if (permission === "denied") {
    return (
      <Card style={{ padding: "18px 20px" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 8 }}>
          Notificaciones
        </div>
        <div
          style={{
            background: P.danger.pastel,
            border:     `1.5px solid ${P.danger.tint}`,
            borderRadius: 12,
            padding:    "14px 16px",
            fontSize: 13,
            color: P.danger.text,
            fontFamily: FF,
            lineHeight: 1.6,
          }}
        >
          <strong>Permiso bloqueado.</strong> Para activar las notificaciones, ve a
          Ajustes del navegador &gt; Permisos del sitio y permite las notificaciones
          para Arbo.
        </div>
      </Card>
    );
  }

  // ── Caso 4: Aún no suscrito ──
  if (!isSubscribed) {
    return (
      <Card style={{ padding: "18px 20px" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 6 }}>
          Notificaciones
        </div>
        <div style={{ fontSize: 12, color: P.textSm, fontFamily: FF, lineHeight: 1.6, marginBottom: 16 }}>
          Activa las notificaciones para recibir alertas de gastos fijos, cortes de
          tarjeta, presupuestos y más — directo en tu dispositivo.
        </div>

        {actionFeedback && (
          <div
            style={{
              background: P.danger.pastel,
              border:     `1px solid ${P.danger.tint}`,
              borderRadius: 8,
              padding:    "8px 12px",
              marginBottom: 12,
              fontSize: 12,
              color: P.danger.text,
              fontFamily: FF,
              fontWeight: 600,
            }}
          >
            {actionFeedback}
          </div>
        )}

        <button
          type="button"
          onClick={handleSubscribe}
          disabled={loading}
          style={{
            width:        "100%",
            padding:      "14px 20px",
            borderRadius: 14,
            border:       "none",
            background:   loading ? P.borderMd : P.ingresos.base,
            color:        "#fff",
            fontFamily:   FF,
            fontSize:     14,
            fontWeight:   800,
            cursor:       loading ? "default" : "pointer",
            transition:   "background .2s",
          }}
        >
          {loading ? "Activando..." : "Activar notificaciones"}
        </button>
      </Card>
    );
  }

  // ── Caso 5: Suscrito — mostrar estado + preferencias ──
  return (
    <Card style={{ padding: "18px 20px" }}>
      {/* Encabezado con estado activo */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF }}>
            Notificaciones
          </div>
          <div
            style={{
              display:    "inline-flex",
              alignItems: "center",
              gap:        5,
              marginTop:  4,
              fontSize:   12,
              fontWeight: 700,
              color:      P.success.text,
              fontFamily: FF,
            }}
          >
            <span
              style={{
                width: 8, height: 8, borderRadius: "50%",
                background: P.success.base, display: "inline-block",
              }}
            />
            Notificaciones activadas
          </div>
        </div>

        {/* Botón desactivar */}
        <button
          type="button"
          onClick={handleUnsubscribe}
          disabled={loading}
          style={{
            padding:      "8px 14px",
            borderRadius: 10,
            border:       `1.5px solid ${P.danger.tint}`,
            background:   P.danger.pastel,
            color:        P.danger.text,
            fontFamily:   FF,
            fontSize:     12,
            fontWeight:   700,
            cursor:       loading ? "default" : "pointer",
            opacity:      loading ? 0.6 : 1,
          }}
        >
          {loading ? "..." : "Desactivar"}
        </button>
      </div>

      {/* Feedback temporal */}
      {actionFeedback && (
        <div
          style={{
            background:   P.success.pastel,
            border:       `1px solid ${P.success.tint}`,
            borderRadius: 8,
            padding:      "8px 12px",
            marginBottom: 14,
            fontSize:     12,
            color:        P.success.text,
            fontFamily:   FF,
            fontWeight:   600,
          }}
        >
          {actionFeedback}
        </div>
      )}

      {/* Botón de prueba — solo en entorno de desarrollo */}
      {import.meta.env.DEV && (
        <button
          type="button"
          onClick={handleTest}
          style={{
            width:        "100%",
            padding:      "10px 16px",
            borderRadius: 12,
            border:       `1.5px solid ${P.fijos.tint}`,
            background:   P.fijos.pastel,
            color:        P.fijos.text,
            fontFamily:   FF,
            fontSize:     12,
            fontWeight:   700,
            cursor:       "pointer",
            marginBottom: 16,
          }}
        >
          Enviar notificacion de prueba
        </button>
      )}

      {/* Lista de preferencias por tipo */}
      <div style={{ fontSize: 11, fontWeight: 800, color: P.textSm, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, fontFamily: FF }}>
        Tipos de alertas
      </div>

      {!prefsLoaded ? (
        <div style={{ fontSize: 12, color: P.textSm, fontFamily: FF, padding: "8px 0" }}>
          Cargando preferencias...
        </div>
      ) : (
        <div>
          {NOTIF_TYPES.map(({ key, label, desc }, index) => (
            <div
              key={key}
              style={index === NOTIF_TYPES.length - 1 ? { borderBottom: "none" } : {}}
            >
              <PreferenceToggle
                notifKey={key}
                label={label}
                desc={desc}
                // Por defecto, todos los tipos están activados si no hay preferencia guardada
                enabled={preferences[key] !== false}
                onChange={handleTogglePreference}
              />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
